import { NoxusAlert } from "@/components/UI/NoxusAlert";
import { Control, Controller, RegisterOptions } from "react-hook-form";
import { useCallback, useMemo } from "react";
import { Button } from "@/components/shadcn/button";
import { NoxusInput } from "@/components/Form/InputField";
import { IODefinition } from "@/types/spotflow";
import { findMaxStringValue } from "@/utils/generic";
import { Plus, Trash } from "lucide-react";

import { SpotSelect } from "./SelectField";
import {
  BoolOutputType,
  DateOutputType,
  FileOutputType,
  NumberOutputType,
  SelectOutputType,
  SmallTextOutputType,
  StringOutputType,
} from "@/types/api/spotflow/spotflow-config";
import { RichTextValue } from "./RichTextField";

import { cn } from "@/lib/utils";
import { NoxusTooltip } from "../UI/NoxusTooltip";
import TiptapEditor from "./TiptapEditor/TiptapEditor";
import { NoxusFormLabel } from "./NoxusFormLabel";
import { type AdvancedChipOption } from "./TiptapEditor/chipOptionsConfig";
import { StringArrayInput } from "./StringArrayField";
import { NumberInput } from "./NumberInputField";
import { DatePickerInput } from "./DatePickerField";
import { Toggle } from "./ToggleField";

const KEYS = {
  text: [
    {
      value: "initial_value",
      label: "Initial value",
      type: "rich_text",
      col_span: 12,
    },
    { value: "required", label: "Required", type: "checkbox", col_span: 12 },
  ],
  small_text: [
    {
      value: "initial_value",
      label: "Initial value",
      type: "text",
      col_span: 12,
    },
    { value: "required", label: "Required", type: "checkbox", col_span: 12 },
  ],
  select: [
    {
      value: "initial_value",
      label: "Initial value",
      type: "select",
      col_span: 12,
    },
    { value: "options", label: "Options", type: "text_array", col_span: 12 },
    { value: "required", label: "Required", type: "checkbox", col_span: 12 },
  ],
  number: [
    {
      value: "initial_value",
      label: "Initial value",
      type: "number",
      col_span: 12,
    },
    { value: "required", label: "Required", type: "checkbox", col_span: 12 },
  ],
  date: [
    {
      value: "initial_value",
      label: "Initial value",
      type: "date",
      col_span: 12,
    },
    {
      value: "has_time",
      label: "Include time",
      type: "checkbox",
      col_span: 12,
    },
    { value: "required", label: "Required", type: "checkbox", col_span: 12 },
  ],
};

export type HumanLoopOutputType =
  | BoolOutputType
  | StringOutputType
  | FileOutputType
  | SelectOutputType
  | NumberOutputType
  | DateOutputType
  | SmallTextOutputType;

interface IDictionaryGroupProps {
  title?: string;
  value?: HumanLoopOutputType[];
  connector: IODefinition;
  tooltip?: string;
  onChange: (value: HumanLoopOutputType[]) => void;
  nodeId: string;
  chipOptions?: AdvancedChipOption[];
  isDisabled?: boolean;
}

const OutputSelector = (props: IDictionaryGroupProps) => {
  const outputs = props.connector.keys as string[];
  const { isDisabled } = props;
  const cantDelete = useMemo(
    () => outputs?.length <= 1 || !!isDisabled,
    [outputs?.length, isDisabled]
  );

  const handleAddClick = useCallback(() => {
    if (isDisabled) return;
    const maxOutputValue = findMaxStringValue(
      props.value?.map((i: any) => i.name) || [],
      /^Field (\d+)$/
    );
    const newDictArray = [
      ...(props.value || []),
      {
        type: "select",
        name: `Field ${maxOutputValue + 1}`,
        initial_value: "",
        options: ["Yes", "No"],
      } as SelectOutputType,
    ];

    props.onChange(newDictArray);
  }, [props, isDisabled]);

  const handleDeleteClick = useCallback(
    (indexToRemove: number) => {
      if (cantDelete) return;

      const v = props.value || [];

      const newDictArray = [
        ...v.slice(0, indexToRemove).concat(v.slice(indexToRemove + 1)),
      ];

      props.onChange(newDictArray);
    },
    [cantDelete, props]
  );

  const handleChange = useCallback(
    (
      v: string | RichTextValue | string[] | number | boolean,
      key: string,
      index: number
    ) => {
      if (isDisabled) return;
      let newValue = v;

      if (key === "name" && (!v || (typeof v === "string" && v.length === 0))) {
        newValue = "(empty)";
      }
      const newDictArray = [...(props.value || [])];
      // @ts-ignore
      newDictArray[index][key] = newValue;
      props.onChange(newDictArray);
    },
    [props, isDisabled]
  );

  const getOptions = useCallback(
    (name: string, index: number, type: string) => {
      if (type !== "select") return [];
      if (name === "initial_value") {
        return (
          (props.value?.[index] as SelectOutputType)?.options?.map(
            (option) => ({ label: option, value: option })
          ) || []
        );
      }
      return [];
    },
    [props.value]
  );

  const getShouldShowWarningBanner = useCallback(
    (index: number, dict: any, value: string) => {
      return (
        (props.value?.[index] as SelectOutputType)?.options &&
        !(props.value?.[index] as SelectOutputType)?.options?.includes(
          dict[value]
        ) &&
        !!dict[value]
      );
    },
    [props.value]
  );

  const handleDeleteOption = useCallback(
    (deletedValue: string, index: number) => {
      if (isDisabled) return;
      const newDictArray = [...(props.value || [])];

      if (newDictArray[index].type === "select") {
        if (newDictArray[index].initial_value === deletedValue) {
          newDictArray[index].initial_value = "";
        }

        props.onChange(newDictArray);
      }
    },
    [props, isDisabled]
  );

  return (
    <>
      <h3 className="font-semibold mb-4">{props.title}</h3>

      <div className="flex flex-col gap-4">
        {props.value && (
          <>
            {props.value.map((dict: any, index: number) => (
              <div
                key={`dict-${index}`}
                className="grid grid-cols-12 gap-4 border rounded-lg p-4 relative"
              >
                <div style={{ gridColumn: "span 6" }}>
                  <NoxusInput
                    value={dict["name"] === "(empty)" ? "" : dict["name"]}
                    label="Field label"
                    onChange={(e) => {
                      handleChange(e.target.value, "name", index);
                    }}
                    disable={isDisabled}
                  />
                </div>
                <div style={{ gridColumn: "span 6" }}>
                  <SpotSelect
                    options={[
                      { label: "Text", value: "text" },
                      { label: "Small text", value: "small_text" },
                      { label: "Select", value: "select" },
                      { label: "Number", value: "number" },
                      { label: "Date", value: "date" },
                    ]}
                    value={dict["type"]}
                    label="Field type"
                    onChange={(e) => {
                      const newDictArray = [...(props.value || [])];
                      const currentField = newDictArray[index] as any;
                      currentField.type = e.value;

                      if (e.value === "number") {
                        currentField.initial_value = 0;
                      } else if (e.value === "select") {
                        currentField.initial_value = "";
                        currentField.options = ["Yes", "No"];
                      } else if (e.value === "text") {
                        currentField.initial_value = "";
                      } else if (e.value === "date") {
                        currentField.initial_value = "";
                      } else if (e.value === "small_text") {
                        currentField.initial_value = "";
                      }

                      props.onChange(newDictArray);
                    }}
                    isDisabled={isDisabled}
                  />
                </div>
                {KEYS[dict["type"] as keyof typeof KEYS]?.map(
                  ({ value, label, type, col_span }) => {
                    if (
                      dict["type"] === "checkbox" &&
                      value === "initial_value"
                    ) {
                      return <></>;
                    }
                    return (
                      <div
                        key={`dict-${index}-${value}`}
                        style={{ gridColumn: `span ${col_span}` }}
                      >
                        {type === "text" && (
                          <NoxusInput
                            value={dict[value]}
                            label={label}
                            onChange={(e) => {
                              handleChange(e.target.value, value, index);
                            }}
                            disable={isDisabled}
                          />
                        )}

                        {type === "select" && (
                          <>
                            <SpotSelect
                              value={dict[value] || []}
                              onChange={(e) => {
                                handleChange(e?.value, value, index);
                              }}
                              label={label}
                              options={getOptions(value, index, type)}
                              isClearable
                              placeholder="This option will be pre-filled in the form"
                              isDisabled={isDisabled}
                            />

                            <div
                              className={cn(
                                "transition-all duration-300 ease-in-out opacity-0",
                                getShouldShowWarningBanner(index, dict, value)
                                  ? "mt-2 max-h-[50px] opacity-100"
                                  : "mt-0 max-h-0 opacity-0"
                              )}
                            >
                              <NoxusAlert
                                look="banner"
                                variant="warning"
                                title="The selected option is not available in the options list"
                              />
                            </div>
                          </>
                        )}

                        {type === "text_array" && (
                          <StringArrayInput
                            value={dict[value] || []}
                            onChange={(e) => {
                              handleChange(e, value, index);
                            }}
                            onDeleteEntry={(deletedValue) => {
                              handleDeleteOption(deletedValue, index);
                            }}
                            label={label}
                            isDisabled={isDisabled}
                          />
                        )}

                        {type === "rich_text" && (
                          <div>
                            <NoxusFormLabel label={label} />
                            <TiptapEditor
                              value={dict[value]}
                              onChange={(e) => {
                                handleChange(e, value, index);
                              }}
                              label={label}
                              chipOptions={props.chipOptions}
                            />
                          </div>
                        )}

                        {type === "number" && (
                          <NumberInput
                            value={
                              typeof dict[value] === "number"
                                ? dict[value]
                                : parseFloat(dict[value]) || 0
                            }
                            onChange={(newValue) => {
                              handleChange(newValue, value, index);
                            }}
                            label={label}
                            min={0}
                            isDisabled={isDisabled}
                          />
                        )}

                        {type === "date" && (
                          <DatePickerInput
                            value={dict[value] || ""}
                            onChange={(newValue) => {
                              handleChange(newValue, value, index);
                            }}
                            label={label}
                            isDisabled={isDisabled}
                            includeTime={dict.has_time}
                          />
                        )}

                        {type === "checkbox" && (
                          <Toggle
                            name={value}
                            value={dict[value] || false}
                            onChange={(newValue: boolean) => {
                              handleChange(newValue, value, index);
                            }}
                            label={label}
                            isDisabled={isDisabled}
                          />
                        )}
                      </div>
                    );
                  }
                )}

                <NoxusTooltip
                  content={cantDelete ? "Can't have less than 1 output" : ""}
                  isDisabled={!cantDelete}
                >
                  <Button
                    variant="ghost"
                    size="iconsm"
                    onClick={() => handleDeleteClick(index)}
                    isDisabled={cantDelete || isDisabled}
                    className="absolute -top-3 -right-3 bg-white rounded-full p-1 border border-gray-100 hover:border-blue-100 hover:bg-blue-50 hover:text-blue-950 disabled:text-gray-200 disabled:cursor-not-allowed disabled:bg-gray-50"
                  >
                    <Trash className="size-4" strokeWidth={1.25} />
                  </Button>
                </NoxusTooltip>
              </div>
            ))}
          </>
        )}
        <Button
          variant="outline"
          onClick={handleAddClick}
          className="mx-auto mt-4"
          isDisabled={isDisabled}
        >
          <Plus size="1.25rem" strokeWidth="1.25px" />
          {`Add fields`}
        </Button>
      </div>
    </>
  );
};

interface IOutputSelectorFieldProps {
  name: string;
  tooltip?: string;
  label?: string;
  connector: IODefinition;
  rules?: RegisterOptions;
  control: Control<any>;
  onChangeExternal?: (action: string, options: any) => void;
  defaultValue: any[];
  nodeId: string;
  chipOptions?: AdvancedChipOption[];
  isDisabled?: boolean;
}

export const OutputSelectorField = (props: IOutputSelectorFieldProps) => {
  return (
    <Controller
      control={props.control}
      name={props.name}
      rules={props.rules}
      defaultValue={props.defaultValue}
      render={({ field: { onChange, value } }) => {
        return (
          <OutputSelector
            value={value}
            title={props.label}
            connector={props.connector}
            tooltip={props.tooltip}
            onChange={onChange}
            nodeId={props.nodeId}
            chipOptions={props.chipOptions}
            isDisabled={props.isDisabled}
          />
        );
      }}
    />
  );
};
